// src/pages/SubscriberPlanPage.jsx
// Route: /subscriber/plan
//
// Pricing confirmed against Guardian_Dubai_Operating_Manual_v1.0 and the
// Stripe payment links in Previu_Guardian_Dubai_Ops_Runbook_July2026.
//
// Restyled onto the Navigator portal's shared .page-header/.card/.card-title/
// .badge/.btn classes so plan cards read like the rest of the app.
//
// 30 Jul 2026 — added: a "Current Plan" summary card (enterprise-SaaS-style,
// per Deepu's explicit ask) when a plan is active, and upgrade-only tile
// states — a tile representing the current plan, or any downgrade, is
// disabled with an explanatory caption. This is UX sugar only: the
// authoritative check is GuardianPlanChangeRules.validateChange() on the
// backend (createPlanOrder/verifyPlanOrder) — a disabled tile here is never
// the only thing standing between a subscriber and a downgrade.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriberAppShell } from '../components/SubscriberLayout';
import { useSubscriber } from '../context/SubscriberContext';
import { subscriberAPI } from '../services/api';
import { useToast } from '../context/ToastContext';

const PLANS = [
  {
    id: 'GUARDIAN',
    name: 'Guardian',
    tagline: "We actively manage your parents' health.",
    priceIndividual: 899,
    priceCouple: 1449,
    saveCouple: 349,
    accent: 'var(--green)',
    features: [
      'Annual T2 blood panel + standard imaging',
      '4 Navigator review calls / year',
      'Red flag escalation within 4 hours',
      '2 hospital escort visits / year',
    ],
  },
  {
    id: 'GUARDIAN_SENTINEL',
    name: 'Guardian Sentinel',
    tagline: 'We stand with your family through serious illness.',
    priceIndividual: 1999,
    priceCouple: 3299,
    saveCouple: 699,
    accent: 'var(--brand-accent)',
    features: [
      'Annual T3 blood panel + advanced imaging',
      '12 Navigator calls / year, Medical Director review',
      '2-hour emergency activation',
      'Unlimited escort · full oncology navigation',
    ],
  },
];

const PLAN_RANK = { GUARDIAN: 1, GUARDIAN_SENTINEL: 2 };
const SIZE_RANK = { INDIVIDUAL: 1, COUPLE: 2 };
const PLAN_NAME = { GUARDIAN: 'Guardian', GUARDIAN_SENTINEL: 'Guardian Sentinel' };

function isStillActive(subscriber) {
  if (subscriber?.subscriptionStatus !== 'ACTIVE' || !subscriber?.subscriptionRenewalAt) return false;
  return new Date(subscriber.subscriptionRenewalAt) > new Date();
}

function CurrentPlanCard({ subscriber }) {
  const { toast } = useToast();
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const planLabel = PLAN_NAME[subscriber.subscriptionPlan] || subscriber.subscriptionPlan;
  const sizeLabel = subscriber.householdSize === 'COUPLE' ? 'Couple' : 'Individual';
  const renewalDate = subscriber.subscriptionRenewalAt
    ? new Date(subscriber.subscriptionRenewalAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  async function handleDownloadInvoice() {
    if (downloadingInvoice) return;
    setDownloadingInvoice(true);
    try {
      const res = await subscriberAPI.downloadInvoice(subscriber.subscriberId);
      const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
      if (!win) toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not generate an invoice for your plan.');
    } finally {
      setDownloadingInvoice(false);
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="card-title" style={{ marginBottom: 6 }}>Current Plan</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
            {planLabel} — {sizeLabel}
          </div>
        </div>
        <span className="badge badge-active">● Active</span>
      </div>
      {renewalDate && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 12 }}>
          Renews on <strong>{renewalDate}</strong>
        </div>
      )}
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={handleDownloadInvoice}
        disabled={downloadingInvoice}
        style={{ marginTop: 12 }}
      >
        {downloadingInvoice ? 'Generating…' : '⬇ Download Invoice'}
      </button>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
        You can upgrade your plan or household size below at any time — you'll only pay the difference,
        and your renewal date stays the same. Downgrades aren't available while your plan is active —
        contact your Navigator if you need one.
      </div>
    </div>
  );
}

function HouseholdSizeToggle({ value, onChange, disabled }) {
  const opt = (val, label) => (
    <button
      key={val}
      type="button"
      disabled={disabled}
      onClick={() => onChange(val)}
      className={`btn btn-sm ${value === val ? 'btn-primary' : 'btn-ghost'}`}
      style={{ flex: 1 }}
    >
      {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', gap: 8, maxWidth: 340, marginBottom: 24 }}>
      {opt('INDIVIDUAL', 'Individual (1 parent)')}
      {opt('COUPLE', 'Couple (2 parents)')}
    </div>
  );
}

function PlanTile({ plan, householdSize, subscriber, selected, loading, onSelect }) {
  const price = householdSize === 'COUPLE' ? plan.priceCouple : plan.priceIndividual;
  const active = isStillActive(subscriber);

  // 1 Aug 2026 — root-cause fix, defense-in-depth layer. subscriber.householdSize
  // can be null for a subscriber whose plan was activated before this column
  // was reliably persisted (see V69 migration for the backfill, and
  // SubscriberController.verifyPlanOrder for the write-boundary fix) —
  // normalizing it here too means this display logic is correct even for
  // any row the backfill somehow misses, rather than silently
  // miscomparing a real string against null.
  const currentHouseholdSize = subscriber.householdSize || 'INDIVIDUAL';

  const isCurrent = active && plan.id === subscriber.subscriptionPlan && householdSize === currentHouseholdSize;
  const isDowngrade = active && !isCurrent && (
    PLAN_RANK[plan.id] < PLAN_RANK[subscriber.subscriptionPlan] ||
    SIZE_RANK[householdSize] < SIZE_RANK[currentHouseholdSize]
  );
  const tileDisabled = loading || isCurrent || isDowngrade;

  // 31 Jul 2026 — Option C: an upgrade only charges the price DIFFERENCE
  // between the subscriber's current plan and this one — matches
  // SubscriberController's createPlanOrder exactly, so what's shown here
  // is what checkout will actually charge, not the full sticker price.
  const isUpgrade = active && !isCurrent && !isDowngrade;
  let upgradeDelta = null;
  if (isUpgrade) {
    const currentPlanDef = PLANS.find(p => p.id === subscriber.subscriptionPlan);
    if (currentPlanDef) {
      const currentPrice = currentHouseholdSize === 'COUPLE'
        ? currentPlanDef.priceCouple : currentPlanDef.priceIndividual;
      upgradeDelta = price - currentPrice;
    }
  }

  return (
    <div
      className="card"
      role="button"
      tabIndex={tileDisabled ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={tileDisabled}
      onClick={() => !tileDisabled && onSelect(plan.id)}
      onKeyDown={(e) => { if (!tileDisabled && (e.key === 'Enter' || e.key === ' ')) onSelect(plan.id); }}
      style={{
        flex: '1 1 260px', marginBottom: 0,
        border: `2px solid ${selected ? plan.accent : 'var(--border)'}`,
        cursor: tileDisabled ? 'not-allowed' : 'pointer',
        opacity: (loading && !selected) || isDowngrade ? 0.5 : 1,
        transition: 'border-color 0.15s',
      }}
    >
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
        color: plan.accent, marginBottom: 6,
      }}>
        {plan.name}
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px 0', minHeight: 36 }}>
        {plan.tagline}
      </p>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)' }}>
        AED {(upgradeDelta ?? price).toLocaleString()}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
        {upgradeDelta != null
          ? `upgrade cost — you keep your current renewal date`
          : `per year, ${householdSize === 'COUPLE' ? `couple — save AED ${plan.saveCouple}` : 'individual'}`}
      </div>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.7 }}>
        {plan.features.map((f) => <li key={f}>{f}</li>)}
      </ul>

      {isCurrent && (
        <div style={{ marginTop: 16, fontSize: 13, color: plan.accent, fontWeight: 700 }}>
          ✓ Your current plan
        </div>
      )}
      {isDowngrade && (
        <div style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)' }}>
          Not available — downgrading requires contacting your Navigator
        </div>
      )}
      {selected && loading && (
        <div style={{ marginTop: 16, fontSize: 13, color: plan.accent, fontWeight: 600 }}>
          Setting up checkout…
        </div>
      )}
    </div>
  );
}

export default function SubscriberPlanPage() {
  const { subscriber } = useSubscriber();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [householdSize, setHouseholdSize] = useState(subscriber?.householdSize || 'INDIVIDUAL');
  const [selecting, setSelecting] = useState(null); // plan id currently being ordered
  const [error, setError] = useState('');

  const active = isStillActive(subscriber);

  // Only AED pricing exists today (GuardianPlanPricing.java) — a subscriber
  // registered with country=IN would hit a 400 from createPlanOrder; that's
  // a real gap (no INR pricing published yet), surfaced via the error state
  // below rather than silently guessing a converted price here.
  async function handleSelect(planId) {
    if (selecting) return;
    setSelecting(planId); setError('');
    try {
      const res = await subscriberAPI.createPlanOrder(subscriber.subscriberId, planId, householdSize);
      navigate('/subscriber/checkout', { state: { plan: planId, householdSize, order: res.data } });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error
        || 'Could not start checkout for this plan.';
      setError(msg);
      toast.error(msg);
      setSelecting(null);
    }
  }

  return (
    <SubscriberAppShell breadcrumbs={[{ label: 'My Family', to: '/subscriber/dashboard' }, { label: 'Plan & Billing' }]}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">{active ? 'Plan & Billing' : 'Choose your plan'}</div>
          <div className="page-subtitle">Billed in {subscriber?.currency || 'AED'}, once a year.</div>
        </div>
      </div>

      {active && <CurrentPlanCard subscriber={subscriber} />}

      <HouseholdSizeToggle value={householdSize} onChange={setHouseholdSize} disabled={!!selecting} />

      {error && <div role="alert" className="alert alert-error">{error}</div>}

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {PLANS.map((plan) => (
          <PlanTile
            key={plan.id}
            plan={plan}
            householdSize={householdSize}
            subscriber={subscriber}
            selected={selecting === plan.id}
            loading={!!selecting}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </SubscriberAppShell>
  );
}
